import { OrganisationLookupModel } from '@abb-procure/data';
import { BooleanInput } from '@angular/cdk/coercion';
import { ScrollingModule } from '@angular/cdk/scrolling';
import {
  Component,
  DestroyRef,
  ElementRef,
  booleanAttribute,
  computed,
  effect,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { Subscription } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { FormRowDirective } from '../../directives/form-row.directive';
import { ButtonGroup } from '../button-group/button-group.component';
import { Button } from '../button/button.component';
import { FilterItem } from '../filter-item/filter-item.component';
import { InputOrganisation } from '../input-organisation/input-organisation.component';

@Component({
  selector: 'procure-filter-organisations',
  templateUrl: './filter-organisations.component.html',
  styleUrl: './filter-organisations.component.scss',
  imports: [
    ReactiveFormsModule,
    Button,
    FormRowDirective,
    ButtonGroup,
    MatIcon,
    ScrollingModule,
    InputOrganisation,
    MatTooltip,
  ],
  providers: [{ provide: FilterItem, useExisting: FilterOrganisations }],
})
export class FilterOrganisations extends FilterItem {
  readonly container =
    viewChild.required<ElementRef<HTMLDivElement>>('container');

  readonly overlay = viewChild<ElementRef>('overlay');

  readonly control = input.required<FormGroup>();
  readonly label = input.required<string>();

  readonly primary = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly external = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly organisations = input<OrganisationLookupModel | null>(null);

  readonly hasPermissions = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly onlyLocalDivision = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly tooltip = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly all = signal(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected readonly controlValue = signal<any>(null);

  protected readonly filterOrganisations = computed(() =>
    this.controlValue()
      ? [
          ...(this.controlValue().divisions ?? []),
          ...(this.controlValue().hubs ?? []),
          ...(this.controlValue().localDivisions ?? []),
        ]
      : [],
  );

  protected readonly hasValue = computed(
    () => this.filterOrganisations().length > 0,
  );

  protected readonly remainder = computed(() => {
    if (!this.filterOrganisations()) {
      return null;
    }

    return this.filterOrganisations().length;
  });

  protected readonly tooltipValue = computed(() => {
    const value = this.filterOrganisations();
    const items = Math.max(value.length, 0);

    return items + (items !== 1 ? ' items selected.' : ' item selected.');
  });

  protected readonly activeColor = computed(() =>
    this.primary() ? 'primary' : 'black',
  );

  protected readonly expanded = signal<string[]>([]);
  protected readonly isLoading = signal(true);
  protected readonly isOpened = signal(false);
  protected readonly id = uuid();
  protected readonly alignment = signal('left');
  protected readonly allSelected = signal(false);

  private valueSubscription?: Subscription;
  private ignoreClick: boolean = true;
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    super();

    this.onGlobalClick = this.onGlobalClick.bind(this);

    effect(() => {
      const control = this.control();

      this.valueSubscription?.unsubscribe();
      this.controlValue.set(control.value);

      this.valueSubscription = control.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((value) => {
          this.controlValue.set(value);
        });
    });

    effect((cleanup) => {
      if (this.isOpened()) {
        this.checkPosition();
        document.addEventListener('click', this.onGlobalClick);
      } else {
        document.removeEventListener('click', this.onGlobalClick);
      }

      cleanup(() => {
        document.removeEventListener('click', this.onGlobalClick);
      });
    });
  }

  protected toggleOverlay(): void {
    this.isOpened.update((value) => !value);
  }

  protected reset(): void {
    this.control().patchValue({ divisions: [], hubs: [], localDivisions: [] });
  }

  protected selectAll(): void {
    this.all.set(true);
  }

  protected afterAllSelected(): void {
    this.all.set(false);
  }

  private onGlobalClick(event: MouseEvent): void {
    if (this.ignoreClick) {
      this.ignoreClick = false;
      return;
    }

    let element = event.target as HTMLElement;

    while (element && element.id !== this.id) {
      element = element.parentElement as HTMLElement;
    }

    if (element) {
      return;
    }

    this.isOpened.set(false);
    document.removeEventListener('click', this.onGlobalClick);
  }

  private checkPosition(): void {
    const { nativeElement } = this.container();

    const { left } = nativeElement.getBoundingClientRect();
    const width = Math.max(nativeElement.offsetWidth, 320);

    this.alignment.set(
      left + width > document.body.offsetWidth ? 'right' : 'left',
    );
  }
}
